import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { formatCurrency } from "../config/market";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Button,
  Chip,
  Tooltip,
  Modal,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import SpaIcon from "@mui/icons-material/Spa";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import StarIcon from "@mui/icons-material/Star";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import {
  addToCart,
  setCartItemQuantity,
  removeFromCart,
} from "../redux/restaurantCartSlice";
import MagnifiableImage from "./MagnifiableImage";
import ProductImageGallery from "../components/ProductImageGallery";
import ProtectedImage from "../components/ProtectedImage";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";
import useCoverage from "../redux/useCoverage";
import { getOptimizedUrl } from "../utils/cloudinaryOptimize";
import DealTag from "../components/DealTag";
import PriceCut, { OfferMeta } from "../components/PriceCut";
import { hasSalePrice, unitPriceOf, unitSavingsOf } from "../utils/vendorDiscounts";
import { availabilityAllowsOrdering } from "../utils/restaurantAvailability";
import OutletSwitchDialog from "../components/OutletSwitchDialog";
import { cartLocationOf } from "../utils/branchCart";
import { locationKeyOf } from "../utils/branchAvailability";

const RestaurantProductDialog = ({
  open,
  onClose,
  product,
  restaurant,
  outletId = null,
  orderingDisabled = false,
  orderingDisabledTitle = "Ordering unavailable",
  orderingDisabledMessage = "",
  onCheckAvailability,
  // Branch operations (4 Oct 2026): the branch this dish is ordered from and
  // the one the cart is already on, for "Move your cart to ...?", and what
  // the page does once the cart has moved (the cart check there).
  branchName = "",
  cartBranchName = "",
  onCartMoved,
  // Shown under the "cannot be ordered" notice: the page's "Get it from"
  // button for a dish this branch cannot supply (shopper journey audit,
  // 4 Oct 2026), so the notice is not a dead end.
  branchAction = null,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const restaurantId = useSelector(
    (state) => state.restaurantCart.currentRestaurant
  );
  const restaurantCart = useSelector((state) =>
    restaurantId ? state.restaurantCart.carts[restaurantId] || {} : {}
  );
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  // Full-image lightbox: the poster hero deliberately cover-crops for the
  // cinematic look, so this is the escape hatch that shows every edge of the
  // photo (c_limit, so it is scaled and never cropped).
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // The add waiting on "Move your cart to <branch>?", or null.
  const [pendingMove, setPendingMove] = useState(null);

  // AUTHORITATIVE deliverability (GET /api/coverage) is the same road-distance
  // + radius verdict the order-create 422 gate makes, resolved against the id
  // the ORDER pipeline uses (owner || _id, outlets resolve their parent). The
  // shop page pre-warms this cache entry, so this normally costs no request.
  // Replaces the old client-side haversine outlet check, which used a 5 km
  // default radius and its own outlet logic and could disagree with checkout.
  const coverageVendorId = restaurant?.owner || restaurant?._id;
  const coverage = useCoverage({
    vendorId: coverageVendorId,
    // The branch the dish is being ordered from, when the shop is one.
    outletId,
    vendorType: "restaurant",
    enabled: Boolean(coverageVendorId),
  });
  const isOutsideDeliveryZone = coverage.isOutOfRange;
  const coverageKm = Number.isFinite(Number(coverage.distanceKm))
    ? Number(coverage.distanceKm).toFixed(1)
    : null;

  const hasOptions = Boolean(product?.optionGroups?.length);
  const existingCartItem = product?._id ? restaurantCart[product._id] : null;
  const isAlreadyInCart = Boolean(existingCartItem);

  const cartItemCount = Object.values(restaurantCart || {}).reduce(
    (sum, item) => sum + (item?.quantity || 0),
    0
  );

  /* "Update to 1" on an item already sitting at 1 is a button that does
     nothing, dressed as the primary action. Knowing whether anything actually
     changed lets the footer offer Remove instead, which is the thing somebody
     staring at that screen actually wants. Options count too: swapping a
     choice without touching the stepper is still a real update. */
  const optionIdsSignature = (options) =>
    (options || [])
      .map((option) => String(option?._id || ""))
      .filter(Boolean)
      .sort()
      .join("|");
  const selectedOptionsSignature = optionIdsSignature(
    Object.values(selectedOptions).flat()
  );
  const quantityUnchanged =
    isAlreadyInCart &&
    quantity === existingCartItem?.quantity &&
    selectedOptionsSignature === optionIdsSignature(existingCartItem?.options);

  const handleGoToCart = () => {
    onClose();
    history.push("/cart");
  };

  useEffect(() => {
    if (!product) return;

    setQuantity(existingCartItem?.quantity || 1);

    if (!existingCartItem?.options?.length || !product.optionGroups?.length) {
      setSelectedOptions({});
      return;
    }

    const existingOptionIds = new Set(
      existingCartItem.options.map((option) => option?._id).filter(Boolean)
    );
    const restoredSelections = product.optionGroups.reduce((acc, group) => {
      const matches = (group.options || []).filter((option) =>
        existingOptionIds.has(option._id)
      );
      if (matches.length > 0) {
        acc[group._id] = matches;
      }
      return acc;
    }, {});

    setSelectedOptions(restoredSelections);
  }, [product, existingCartItem]);

  const totalPrice = useMemo(() => {
    if (!product) return 0;

    // What the customer pays per dish: the sale price while an offer is live.
    const base = unitPriceOf(product) * quantity;
    const optionsTotal = Object.values(selectedOptions)
      .flat()
      .filter(Boolean)
      .reduce((sum, option) => sum + (option?.price || 0), 0);

    return base + optionsTotal * quantity;
  }, [product, quantity, selectedOptions]);

  const requiredSelectionsSatisfied = useMemo(() => {
    if (!product?.optionGroups?.length) return true;

    return product.optionGroups.every((group) => {
      if (!group.minOptions) return true;
      const selectedCount = selectedOptions[group._id]?.length || 0;
      return selectedCount >= group.minOptions;
    });
  }, [product, selectedOptions]);

  // Plain-text first line of the (possibly rich-HTML) description, for the
  // hero overlay. Regex strip keeps this dependency-free; the full formatted
  // story still renders below in the Dish story card.
  const heroTagline = useMemo(() => {
    const text = String(product?.description || "")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!text) return "";
    return text.length > 92 ? `${text.slice(0, 92).trimEnd()}…` : text;
  }, [product]);

  const handleQuantityChange = (delta) => {
    setQuantity((prev) => {
      const next = prev + delta;
      return next < 1 ? 1 : next;
    });
  };

  const toggleOption = (groupId, option) => {
    setSelectedOptions((prev) => {
      const current = prev[groupId] || [];
      const exists = current.some((item) => item._id === option._id);

      if (exists) {
        // Don't allow deselection if it would violate minOptions
        const group = product.optionGroups.find((g) => g._id === groupId);
        const minRequired = group?.minOptions || 0;
        if (minRequired > 0 && current.length <= minRequired) {
          // Can't deselect - would go below minimum, just replace with new selection
          return prev;
        }
        return {
          ...prev,
          [groupId]: current.filter((item) => item._id !== option._id),
        };
      }

      const group = product.optionGroups.find((g) => g._id === groupId);
      const max = group?.maxOptions;

      let updated = [...current, option];
      // If maxOptions is set (greater than 0), enforce the limit
      if (max && max > 0 && updated.length > max) {
        // Replace selection instead of adding (for single-select scenarios like "pick one drink")
        updated = [option];
      }

      return {
        ...prev,
        [groupId]: updated,
      };
    });
  };

  /* Taking an item OUT was impossible from this dialog: the minus button was
     `disabled={quantity === 1}`, so a dish already in the cart could only ever
     be increased. The only exit was the cart screen, which is a strange place
     to have to go to undo a tap you just made. At quantity 1 the control now
     removes instead of decrementing. */
  const handleRemoveFromCart = () => {
    if (!product?._id || !restaurantId) return;
    dispatch(removeFromCart({ restaurantId, productId: product._id }));
    onClose();
  };

  const handleAddToCart = async () => {
    if (orderingDisabled) {
      setAvailabilityError(orderingDisabledMessage);
      return;
    }
    if (onCheckAvailability) {
      setCheckingAvailability(true);
      setAvailabilityError("");
      /* The await used to be open-ended. On a slow connection the button sat
         reading "Checking restaurant hours…" with nothing moving, which is
         indistinguishable from a frozen page — and the tap had in fact
         registered, so people tap again. Losing the race means we go ahead:
         the order-create gate re-checks hours server-side and answers 422, so
         a slow check delays an order it can no longer prevent. */
      const latest = await Promise.race([
        Promise.resolve(onCheckAvailability()).catch(() => null),
        new Promise((resolve) =>
          setTimeout(() => resolve({ available: true, timedOut: true }), 6000)
        ),
      ]);
      setCheckingAvailability(false);
      if (!availabilityAllowsOrdering(latest)) {
        setAvailabilityError(
          latest?.message ||
            "This restaurant is not accepting orders right now."
        );
        return;
      }
    }
    const aggregatedOptions = Object.values(selectedOptions).flat();

    const cartPayload = {
      restaurantId,
      product: {
        ...product,
        options: aggregatedOptions,
      },
      quantity,
      options: aggregatedOptions,
      // The branch this dish is ordered from, named outright (null is the
      // main branch): one restaurant cart holds ONE branch (4 Oct 2026).
      outletId: outletId || null,
    };

    /* A cart already on another branch of this restaurant asks first. On a
       yes the whole cart moves here (the slice does it) and is checked at
       this branch; on a no nothing is added. */
    const cartElsewhere =
      Object.keys(restaurantCart || {}).length > 0 &&
      cartLocationOf(restaurantCart) !== locationKeyOf(outletId || null);
    if (cartElsewhere) {
      setPendingMove(cartPayload);
      return;
    }

    applyCartPayload(cartPayload);
  };

  const applyCartPayload = (cartPayload, { moved = false } = {}) => {
    dispatch(
      isAlreadyInCart
        ? setCartItemQuantity(cartPayload)
        : addToCart(cartPayload)
    );
    if (moved && typeof onCartMoved === "function") onCartMoved();
    onClose();
  };

  if (!product) return null;

  const onOffer = hasSalePrice(product);

  const badges = [
    {
      icon: <LocalDiningIcon fontSize="small" />,
      label: product?.mealType || "Chef's pick",
      tone: "primary",
    },
    product?.isSignature
      ? {
        icon: <StarIcon fontSize="small" />,
        label: "Signature Dish",
        tone: "secondary",
      }
      : null,
    product?.isVegetarian
      ? {
        icon: <SpaIcon fontSize="small" />,
        label: "Vegetarian Friendly",
        tone: "success",
      }
      : null,
  ].filter(Boolean);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          overflow: "hidden",
          width: "min(1080px, calc(100vw - 48px))",
          maxHeight: "min(760px, calc(100vh - 56px))",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.32)",
          // Full-screen sheet on phones: no floating card, no wasted margin,
          // and 100dvh keeps the footer visible above mobile browser chrome.
          "@media (max-width: 640px)": {
            width: "100vw",
            height: "100dvh",
            maxHeight: "100dvh",
            margin: 0,
            borderRadius: 0,
          },
        },
      }}
    >
      <DialogTitleStyled>
        <TitleRow>
          <TitleContent>
            <TitleIconWrapper>
              <RestaurantMenuIcon fontSize="small" />
            </TitleIconWrapper>
            <div>
              <Typography variant="h6" component="div">
                {product.name || "Untitled Meal"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {product.category || "Curated restaurant meal"}
              </Typography>
            </div>
          </TitleContent>
          <IconButtonStyled onClick={onClose} size="large">
            <CloseIcon />
          </IconButtonStyled>
        </TitleRow>
      </DialogTitleStyled>

      <DialogContentStyled>
        <ContentGrid>
          <ProductColumn>
            <ImagePanel>
              {/* Editorial poster hero: a tall cinematic frame, crop biased
                  toward the plate (object-position center 38%), dish name set
                  over a bottom gradient and badges overlaid on the photo.
                  Built inside the gallery's renderMain prop so the overlay
                  travels with whichever angle is active; every overlay layer
                  is pointer-events:none so hover-zoom keeps working. Video
                  stages are untouched. */}
              <ImageFrame>
                <ProductImageGallery
                  images={[
                    product.image,
                    ...(Array.isArray(product.additionalImages)
                      ? product.additionalImages
                      : []),
                  ]}
                  videos={product.videos}
                  alt={product.name}
                  /* Dish thumbnails crop, they do not letterbox: a plate fills
                     its tile. Products elsewhere contain, so the packaging is
                     never cut. Hover zoom and the enlarge control stay off
                     here because this hero already has both, MagnifiableImage
                     for the lens and its own full-image lightbox. */
                  thumbFit="cover"
                  renderMain={(currentUrl) =>
                    currentUrl ? (
                      <HeroStage>
                        <MagnifiableImage
                          src={getOptimizedUrl(currentUrl, "productHero")}
                          alt={product.name}
                          zoomLevel={1.15}
                          className="restaurant-product-hero"
                        />
                        <HeroScrim aria-hidden="true" />
                        {onOffer && (
                          <DealTag
                            discount={product.discount}
                            size="l"
                            radius={18}
                            zIndex={3}
                          />
                        )}
                        <HeroBadges aria-hidden="true" $besideDeal={onOffer}>
                          {badges.map((badge) => (
                            <HeroChip key={badge.label} $tone={badge.tone}>
                              {badge.icon}
                              <span>{badge.label}</span>
                            </HeroChip>
                          ))}
                        </HeroBadges>
                        <HeroMeta aria-hidden="true">
                          <HeroName>{product.name}</HeroName>
                          {heroTagline && <HeroTagline>{heroTagline}</HeroTagline>}
                        </HeroMeta>
                        <Tooltip title="View full image">
                          <HeroExpandButton
                            type="button"
                            aria-label="View full image"
                            onClick={() =>
                              setLightboxUrl(getOptimizedUrl(currentUrl, "fullView"))
                            }
                          >
                            <ZoomOutMapIcon fontSize="small" />
                          </HeroExpandButton>
                        </Tooltip>
                      </HeroStage>
                    ) : (
                      <NoImagePanel>
                        <RestaurantMenuIcon fontSize="large" />
                      </NoImagePanel>
                    )
                  }
                />
              </ImageFrame>
            </ImagePanel>

            <InfoPanel>
              <DetailSummary>
                <div>
                  <SectionTitle>Item details</SectionTitle>
                  <Typography variant="body2">
                    Review the dish, choose any required options, then add it
                    to your restaurant cart.
                  </Typography>
                </div>
                <PriceColumn>
                  <PricePill>
                    <PriceCut
                      component="strong"
                      price={product.price}
                      listPrice={product.listPrice}
                      salePrice={product.salePrice}
                      format={formatCurrency}
                    />
                  </PricePill>
                  {onOffer && (
                    <OfferMeta
                      savings={unitSavingsOf(product)}
                      endsAt={product.discount?.endsAt}
                      format={formatCurrency}
                    />
                  )}
                </PriceColumn>
              </DetailSummary>

              <DescriptionSection>
                <SectionTitle>Dish story</SectionTitle>
                <ReactQuill
                  value={
                    product.description || "Chef is still writing this story."
                  }
                  readOnly
                  theme="bubble"
                />
              </DescriptionSection>

              {product?.chefNotes && (
                <ChefNotes>
                  <Typography variant="subtitle2">Chef's notes</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {product.chefNotes}
                  </Typography>
                </ChefNotes>
              )}
            </InfoPanel>
          </ProductColumn>

          <OptionsColumn>
            <OrderingPanel>
              <OptionsCard>
                <OptionsHeader>
                  <Typography variant="subtitle1">Customize</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Tailor the dish to your taste
                  </Typography>
                </OptionsHeader>

                {hasOptions ? (
                  <OptionsStack>
                    {product.optionGroups.map((group) => {
                      const selectedCount =
                        selectedOptions[group._id]?.length || 0;
                      const isSatisfied = group.minOptions
                        ? selectedCount >= group.minOptions
                        : true;

                      return (
                        <OptionsGroup key={group._id}>
                          <OptionsGroupHeader>
                            <Typography variant="subtitle2">
                              {group.name}
                            </Typography>
                            <GroupMeta>
                              {group.minOptions > 0 && (
                                <RequiredChip
                                  label="Required"
                                  isOk={isSatisfied}
                                />
                              )}
                              {group.maxOptions > 0 && (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  {group.maxOptions === 1 ? "Pick one" : `Up to ${group.maxOptions}`}
                                </Typography>
                              )}
                            </GroupMeta>
                          </OptionsGroupHeader>

                          <OptionsGrid>
                            {group.options?.map((option) => {
                              const isSelected = selectedOptions[group._id]?.some(
                                (item) => item._id === option._id
                              );
                              // A pick-one group behaves like radio buttons:
                              // tapping another option swaps the choice
                              // (toggleOption already replaces at the limit).
                              // Disabling the other tiles, as this did, left
                              // a customer who chose Medium unable to change
                              // to Large without closing the dialog. Only a
                              // pick-several group disables once full.
                              const reachedMax =
                                group.maxOptions > 1 &&
                                !isSelected &&
                                selectedCount >= group.maxOptions;

                              return (
                                <OptionTile
                                  key={option._id}
                                  $selected={isSelected}
                                  disabled={reachedMax}
                                  onClick={() => toggleOption(group._id, option)}
                                >
                                  <div>
                                    <OptionName>{option.name}</OptionName>
                                    {option.description && (
                                      <OptionDescription>
                                        {option.description}
                                      </OptionDescription>
                                    )}
                                  </div>
                                  <OptionMeta>
                                    {option.price > 0 && (
                                      <OptionPrice>
                                        +{formatCurrency(option.price)}
                                      </OptionPrice>
                                    )}
                                    {isSelected && (
                                      <CheckCircleIcon
                                        sx={{ color: "#4ade80" }}
                                        fontSize="small"
                                      />
                                    )}
                                  </OptionMeta>
                                </OptionTile>
                              );
                            })}
                          </OptionsGrid>
                        </OptionsGroup>
                      );
                    })}
                  </OptionsStack>
                ) : (
                  <EmptyOptions>
                    No customization required. You're good to go.
                  </EmptyOptions>
                )}
              </OptionsCard>

              <FooterCard>
                {/* Delivery Zone Warning */}
                {isOutsideDeliveryZone && (
                  <DeliveryWarning>
                    <LocationOffIcon />
                    <div>
                      <strong>Outside Delivery Zone</strong>
                      <p>
                        This restaurant doesn't deliver to your location
                        {coverageKm ? ` (${coverageKm} km away by road` : ''}
                        {coverageKm && coverage.radius ? `, max ${coverage.radius} km)` : coverageKm ? ')' : ''}
                      </p>
                    </div>
                  </DeliveryWarning>
                )}
                {(orderingDisabled || availabilityError) && (
                  <DeliveryWarning>
                    <div>
                      <strong>{orderingDisabledTitle}</strong>
                      <p>
                        {availabilityError ||
                          orderingDisabledMessage ||
                          "This restaurant is not accepting orders right now."}
                      </p>
                      {orderingDisabled ? branchAction : null}
                    </div>
                  </DeliveryWarning>
                )}

                {isAlreadyInCart && (
                  <CartStatus>
                    <CheckCircleIcon fontSize="small" />
                    <div>
                      <strong>Already in cart</strong>
                      <span>Change the quantity here to update this item.</span>
                    </div>
                  </CartStatus>
                )}

                <QuantityRow>
                  <Typography variant="subtitle2">
                    {isAlreadyInCart ? "Quantity in cart" : "Quantity"}
                  </Typography>
                  <QuantityControls>
                    <QuantityButton
                      onClick={
                        quantity === 1 && isAlreadyInCart
                          ? handleRemoveFromCart
                          : () => handleQuantityChange(-1)
                      }
                      disabled={quantity === 1 && !isAlreadyInCart}
                      aria-label={
                        quantity === 1 && isAlreadyInCart
                          ? "Remove from cart"
                          : "Decrease quantity"
                      }
                      title={
                        quantity === 1 && isAlreadyInCart
                          ? "Remove from cart"
                          : "Decrease quantity"
                      }
                    >
                      {quantity === 1 && isAlreadyInCart ? (
                        <DeleteOutlineIcon />
                      ) : (
                        <RemoveIcon />
                      )}
                    </QuantityButton>
                    <Typography variant="h6">{quantity}</Typography>
                    <QuantityButton onClick={() => handleQuantityChange(1)}>
                      <AddIcon />
                    </QuantityButton>
                  </QuantityControls>
                </QuantityRow>

                <TotalRow>
                  <div>
                    <TotalLabel variant="caption">Total</TotalLabel>
                    <Typography variant="h5" component="p">
                      {formatCurrency(totalPrice)}
                    </Typography>
                  </div>
                  <Tooltip
                    title={
                      orderingDisabled || availabilityError
                        ? availabilityError || orderingDisabledMessage
                        : isOutsideDeliveryZone
                        ? "This restaurant doesn't deliver to your location"
                          : requiredSelectionsSatisfied
                            ? quantityUnchanged
                              ? "Remove this dish from your cart"
                              : isAlreadyInCart
                                ? "Update quantity"
                                : "Add to cart"
                            : "Kindly complete the required selections"
                    }
                  >
                    <span>
                      <PrimaryButton
                        onClick={
                          quantityUnchanged
                            ? handleRemoveFromCart
                            : handleAddToCart
                        }
                        $danger={quantityUnchanged}
                        disabled={
                          !requiredSelectionsSatisfied ||
                          isOutsideDeliveryZone ||
                          orderingDisabled ||
                          checkingAvailability
                        }
                        fullWidth
                        startIcon={
                          checkingAvailability ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : quantityUnchanged ? (
                            <DeleteOutlineIcon />
                          ) : null
                        }
                      >
                        {checkingAvailability
                          ? "Checking restaurant hours…"
                          : orderingDisabled || availabilityError
                            ? orderingDisabledTitle
                            : isOutsideDeliveryZone
                          ? "Cannot Deliver to Your Location"
                          : /* Nothing changed, so "Update to 1" would be a
                               button that does nothing. Offer the action
                               somebody on this screen actually wants. */
                            quantityUnchanged
                              ? "Remove from cart"
                              : isAlreadyInCart
                                ? `Update to ${quantity}`
                                : `Add ${quantity} item${quantity > 1 ? "s" : ""}`}
                      </PrimaryButton>
                    </span>
                  </Tooltip>
                </TotalRow>

                {cartItemCount > 0 && (
                  <GoToCartButton
                    onClick={handleGoToCart}
                    fullWidth
                    startIcon={<ShoppingCartIcon />}
                  >
                    Go to Cart ({cartItemCount})
                  </GoToCartButton>
                )}
              </FooterCard>
            </OrderingPanel>
          </OptionsColumn>
        </ContentGrid>
      </DialogContentStyled>

      {/* Full-image lightbox: contain-fit, every edge visible. Backdrop
          click, the X and Escape (via MUI Modal) all dismiss. */}
      <Modal
        open={!!lightboxUrl}
        onClose={() => setLightboxUrl(null)}
        sx={{ zIndex: 2400 }}
      >
        <LightboxRoot onClick={() => setLightboxUrl(null)}>
          {lightboxUrl && (
            <LightboxImage
              src={lightboxUrl}
              alt={product.name}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <LightboxClose
            type="button"
            aria-label="Close full image"
            onClick={() => setLightboxUrl(null)}
          >
            <CloseIcon fontSize="small" />
          </LightboxClose>
        </LightboxRoot>
      </Modal>
      <OutletSwitchDialog
        open={Boolean(pendingMove)}
        fromLabel={cartBranchName}
        toLabel={branchName}
        onCancel={() => setPendingMove(null)}
        onConfirm={() => {
          const payload = pendingMove;
          setPendingMove(null);
          if (payload) applyCartPayload(payload, { moved: true });
        }}
      />
    </Dialog>
  );
};

export default RestaurantProductDialog;

const DialogTitleStyled = styled(DialogTitle)`
  padding: 20px 28px !important;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  background: #ffffff;

  @media (max-width: 640px) {
    padding: 18px 20px !important;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const TitleContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;

  h6 {
    line-height: 1.2;
  }

  h6,
  p {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TitleIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(239, 59, 71, 0.15),
    rgba(239, 59, 71, 0.05)
  );
  display: grid;
  place-items: center;
  color: #ef3b47;
`;

const IconButtonStyled = styled(IconButton)`
  border-radius: 10px !important;
  transition: transform 0.25s ease, background-color 0.25s ease !important;

  &:hover {
    transform: rotate(90deg);
    background-color: rgba(239, 59, 71, 0.12);
  }
`;

const DialogContentStyled = styled(DialogContent)`
  padding: 0 !important;
  overflow: hidden !important;

  @media (max-width: 996px) {
    overflow-y: auto !important;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(340px, 0.92fr);
  gap: 0;
  min-height: 0;
  max-height: min(648px, calc(100vh - 128px));

  @media (max-width: 996px) {
    grid-template-columns: 1fr;
    max-height: none;
  }
`;

const ProductColumn = styled.div`
  position: relative;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  overflow-y: auto;
  background: radial-gradient(
      circle at top left,
      rgba(148, 163, 184, 0.12),
      transparent 45%
    ),
    rgba(248, 250, 252, 1);

  @media (max-width: 996px) {
    padding: 24px;
    overflow: visible;
  }

  @media (max-width: 640px) {
    padding: 18px;
  }
`;

const OptionsColumn = styled.div`
  background: #f8fafc;
  border-left: 1px solid rgba(148, 163, 184, 0.22);
  color: rgba(15, 23, 42, 0.92);
  padding: 24px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;

  @media (max-width: 996px) {
    padding: 28px;
    overflow: visible;
    border-left: none;
    border-top: 1px solid rgba(148, 163, 184, 0.22);
  }

  @media (max-width: 640px) {
    padding: 20px 18px 24px;
  }
`;

const ImagePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/* The frame is now a neutral wrapper. All the visual chrome lives on
   HeroStage so the thumbnail rail (multi-image products) sits beside a
   clean poster rather than inside a bordered box. */
const ImageFrame = styled.div`
  width: 100%;
`;

const HeroStage = styled.div`
  position: relative;
  width: 100%;
  height: clamp(320px, 44vh, 470px);
  border-radius: 18px;
  overflow: hidden;
  background: #0f172a;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.16);

  .restaurant-product-hero {
    height: 100%;
  }

  .restaurant-product-hero img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Bias the crop toward the plate, because food shots carry the dish in the
       upper-middle band, garnish and table at the edges. */
    object-position: center 38%;
    transition: transform 0.6s ease;
  }

  &:hover .restaurant-product-hero img {
    transform: scale(1.025);
  }

  @media (max-width: 640px) {
    height: clamp(250px, 38vh, 360px);
    border-radius: 16px;
  }
`;

/* Every overlay layer is pointer-events:none, because the magnify hover-zoom
   underneath must keep receiving the mouse. */
const HeroScrim = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.18) 0%,
    rgba(15, 23, 42, 0) 22%,
    rgba(15, 23, 42, 0) 46%,
    rgba(15, 23, 42, 0.82) 100%
  );
`;

const HeroBadges = styled.div`
  position: absolute;
  z-index: 3;
  top: 14px;
  /* The deal stub (60 wide) sits flush in the corner; the chips step right. */
  left: ${(p) => (p.$besideDeal ? "74px" : "14px")};
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  pointer-events: none;
`;

const HERO_CHIP_TONES = {
  primary: "#1d4ed8",
  secondary: "#9333ea",
  success: "#15803d",
};

const HeroChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  color: ${(p) => HERO_CHIP_TONES[p.$tone] || HERO_CHIP_TONES.primary};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.22);

  svg {
    font-size: 0.95rem;
  }
`;

const HeroMeta = styled.div`
  position: absolute;
  z-index: 3;
  left: 18px;
  right: 18px;
  bottom: 15px;
  color: #ffffff;
  pointer-events: none;
  display: grid;
  gap: 3px;
`;

const HeroName = styled.p`
  margin: 0;
  font-size: clamp(1.15rem, 2.2vw, 1.45rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.45);
`;

/* The one hero overlay that DOES take the pointer: everything else stays
   pointer-events:none for the magnify hover-zoom underneath.

   z-index 100 is not arbitrary. The magnify plugin drops its glass into the
   same stacking context at z-index 99, so at anything lower this button is
   painted over and its clicks are eaten by a circle that follows the cursor
   straight onto it. MagnifiableImage takes the glass out of hit testing;
   this keeps the button visible while the glass passes underneath. */
const HeroExpandButton = styled.button`
  position: absolute;
  z-index: 100;
  right: 14px;
  top: 14px;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(15, 23, 42, 0.45);
  color: #ffffff;
  display: grid;
  place-items: center;
  cursor: zoom-in;
  pointer-events: auto;
  backdrop-filter: blur(6px);
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(15, 23, 42, 0.7);
    transform: scale(1.06);
  }
`;

const LightboxRoot = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px;
  background: rgba(2, 8, 20, 0.93);
  outline: none;
  cursor: zoom-out;
`;

const LightboxImage = styled(ProtectedImage)`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
  cursor: default;
`;

const LightboxClose = styled.button`
  position: fixed;
  top: 20px;
  right: 24px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.26);
  }
`;

const HeroTagline = styled.p`
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: rgba(241, 245, 249, 0.88);
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.4);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NoImagePanel = styled.div`
  min-height: clamp(320px, 44vh, 470px);
  border-radius: 18px;
  display: grid;
  place-items: center;
  color: rgba(15, 23, 42, 0.35);
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
`;

const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailSummary = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);

  p {
    color: rgba(15, 23, 42, 0.62);
    line-height: 1.45;
    margin: 0;
    font-size: 0.9rem;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
  }
`;

/* The pill plus, on a discounted dish, the savings line under it. */
const PriceColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

const PricePill = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(239, 59, 71, 0.1);
  color: #d1342d;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  width: fit-content;
`;

const DescriptionSection = styled.div`
  border-radius: 14px;
  padding: 14px 16px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(148, 163, 184, 0.2);

  .ql-container {
    border: none !important;
  }

  .ql-editor {
    color: rgba(15, 23, 42, 0.78);
    font-size: 0.94rem;
    line-height: 1.55;
    padding: 0;
    max-height: 5.1em;
    overflow: hidden;
  }

  .ql-editor p {
    margin: 0;
  }
`;

const SectionTitle = styled(Typography)`
  font-weight: 700 !important;
  font-size: 0.82rem !important;
  margin-bottom: 8px !important;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(15, 23, 42, 0.45);
`;

const ChefNotes = styled.div`
  border-radius: 14px;
  padding: 16px;
  background: linear-gradient(
    145deg,
    rgba(239, 59, 71, 0.08),
    rgba(15, 23, 42, 0.05)
  );
  border: 1px solid rgba(239, 59, 71, 0.15);
  display: grid;
  gap: 8px;
`;

const OrderingPanel = styled.div`
  width: 100%;
  display: grid;
  gap: 14px;
  align-self: start;
  border-radius: 22px;
  padding: 18px;
  background:
    radial-gradient(circle at top right, rgba(132, 176, 38, 0.18), transparent 32%),
    #0f172a;
  color: rgba(241, 245, 249, 0.95);
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);

  .MuiTypography-colorTextSecondary {
    color: rgba(226, 232, 240, 0.68) !important;
  }

  @media (max-width: 640px) {
    border-radius: 18px;
    padding: 14px;
  }
`;

const OptionsCard = styled.div`
  /* SOLID surface. The old translucent rgba fill plus backdrop blur let the scrolling
     option tiles ghost through the card when a dish had many options. */
  background: #16233d;
  border-radius: 18px;
  border: 1px solid rgba(226, 232, 240, 0.12);
  padding: 20px;
  display: grid;
  gap: 16px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);

  @media (max-width: 640px) {
    padding: 14px;
    border-radius: 16px;
  }
`;

const OptionsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .MuiTypography-caption {
    color: rgba(226, 232, 240, 0.7) !important;
  }
`;

const OptionsStack = styled.div`
  display: grid;
  gap: 12px;
  max-height: min(38vh, 340px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 6px;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.35);
    border-radius: 999px;
  }

  @media (max-width: 996px) {
    max-height: none;
    overflow-y: visible;
    padding-right: 0;
  }
`;

const OptionsGroup = styled.div`
  display: grid;
  gap: 10px;
`;

const OptionsGroupHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const GroupMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .MuiTypography-caption {
    color: rgba(226, 232, 240, 0.66) !important;
  }
`;

const RequiredChip = ({ label, isOk }) => (
  <Chip
    label={label}
    size="small"
    variant={isOk ? "outlined" : "filled"}
    sx={{
      backgroundColor: isOk ? "transparent" : "#f33a3aff",
      borderColor: isOk ? "#4ade80" : "#f03b3bff",
      color: isOk ? "#4ade80" : "#ffffff",
      fontWeight: 600,
    }}
  />
);

const OptionsGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const OptionTile = styled.button`
  width: 100%;
  padding: 12px 14px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  /* Solid tile colours. Translucent tiles were part of the see-through
     effect when the list scrolled behind the card chrome. */
  background: ${(props) => (props.$selected ? "#2b3f66" : "#1b2a47")};
  border: 1.5px solid
    ${(props) =>
    props.$selected
      ? "#84b026"
      : "rgba(148, 163, 184, 0.28)"};
  border-radius: 14px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: border-color 0.18s ease, background 0.18s ease;
  color: rgba(241, 245, 249, 0.95);

  &:hover {
    border-color: ${(props) =>
    props.disabled
      ? "rgba(148, 163, 184, 0.28)"
      : props.$selected
        ? "#84b026"
        : "rgba(226, 232, 240, 0.55)"};
    background: ${(props) =>
    props.disabled ? "#1b2a47" : props.$selected ? "#2b3f66" : "#22345a"};
  }

  &:disabled {
    opacity: 0.45;
    pointer-events: none;
  }

  @media (max-width: 640px) {
    padding: 11px 12px;
    border-radius: 12px;
  }
`;

const OptionName = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
`;

const OptionDescription = styled.p`
  margin: 4px 0 0;
  font-size: 0.85rem;
  opacity: 0.7;
`;

const OptionMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;

const OptionPrice = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: #facc15;
`;

const EmptyOptions = styled.div`
  background: #1b2a47;
  border-radius: 14px;
  padding: 22px 18px;
  text-align: center;
  color: rgba(226, 232, 240, 0.75);
  font-size: 0.95rem;
`;

const FooterCard = styled.div`
  background: #16233d;
  border-radius: 18px;
  border: 1px solid rgba(226, 232, 240, 0.14);
  padding: 18px;
  display: grid;
  gap: 14px;
  box-shadow: none;

  /* On stacked/mobile layouts the whole dialog scrolls, so keep the add-to-cart
     footer pinned so the primary action is always reachable. Solid bg + shadow
     so scrolled content never shows through it. */
  @media (max-width: 996px) {
    position: sticky;
    bottom: 0;
    z-index: 3;
    box-shadow: 0 -12px 28px rgba(15, 23, 42, 0.45);
  }

  @media (max-width: 640px) {
    padding: 14px;
    border-radius: 16px;
  }
`;

const CartStatus = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border-radius: 14px;
  background: rgba(132, 176, 38, 0.12);
  border: 1px solid rgba(132, 176, 38, 0.28);
  color: rgba(241, 245, 249, 0.92);

  svg {
    color: #84b026;
    margin-top: 2px;
  }

  div {
    display: grid;
    gap: 2px;
  }

  strong {
    font-size: 0.9rem;
    line-height: 1.2;
  }

  span {
    color: rgba(226, 232, 240, 0.68);
    font-size: 0.82rem;
    line-height: 1.35;
  }
`;

const QuantityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
`;

const QuantityControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
`;

const QuantityButton = styled(Button)`
  min-width: 42px !important;
  height: 42px !important;
  border-radius: 14px !important;
  background: rgba(15, 23, 42, 0.9) !important;
  border: 1px solid rgba(148, 163, 184, 0.35) !important;
  color: rgba(241, 245, 249, 0.92) !important;

  &:hover {
    background: rgba(239, 59, 71, 0.75) !important;
    border-color: rgba(239, 59, 71, 0.75) !important;
  }

  &:disabled {
    opacity: 0.4 !important;
    border-color: rgba(148, 163, 184, 0.25) !important;
  }
`;

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  > span {
    flex: 0 1 230px;
  }

  @media (max-width: 640px) {
    align-items: stretch;
    flex-direction: column;

    > span {
      flex: 1 1 auto;
    }
  }
`;

const TotalLabel = styled(Typography)`
  color: rgba(226, 232, 240, 0.72) !important;
  display: block;
`;

/* $danger is the REMOVE state. It cannot simply reuse the solid red: this
   button is ALREADY red for "Add to cart", so the same fill on a destructive
   action makes the two indistinguishable at a glance. Outlined instead, still
   unmistakably red and visibly not the primary action. */
const PrimaryButton = styled(Button)`
  text-transform: none !important;
  border-radius: 14px !important;
  padding: 13px 22px !important;
  background: ${(p) =>
    p.$danger
      ? "transparent"
      : "linear-gradient(135deg, #ef3b47 0%, #d1342d 100%)"} !important;
  border: ${(p) => (p.$danger ? "1.5px solid #ef3b47" : "none")} !important;
  color: ${(p) => (p.$danger ? "#d1342d" : "#fff")} !important;
  font-weight: 700 !important;
  box-shadow: ${(p) =>
    p.$danger ? "none" : "0 18px 40px rgba(239, 59, 71, 0.35)"} !important;

  &:hover {
    background: ${(p) =>
      p.$danger
        ? "rgba(239, 59, 71, 0.08)"
        : "linear-gradient(135deg, #d1342d 0%, #bb2a27 100%)"} !important;
  }

  &:disabled {
    background: rgba(148, 163, 184, 0.35) !important;
    color: rgba(15, 23, 42, 0.45) !important;
    box-shadow: none !important;
    cursor: not-allowed !important;
  }
`;

const GoToCartButton = styled(Button)`
  text-transform: none !important;
  border-radius: 14px !important;
  padding: 11px 20px !important;
  background: rgba(255, 255, 255, 0.06) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  border: 1px solid rgba(226, 232, 240, 0.28) !important;

  &:hover {
    background: rgba(255, 255, 255, 0.14) !important;
    border-color: rgba(226, 232, 240, 0.45) !important;
  }
`;

const DeliveryWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border: 1px solid #ef9a9a;
  border-radius: 12px;
  margin-bottom: 16px;

  svg {
    color: #c62828;
    font-size: 24px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  div {
    flex: 1;
  }

  strong {
    color: #c62828;
    font-size: 0.95rem;
    display: block;
    margin-bottom: 4px;
  }

  p {
    color: #b71c1c;
    font-size: 0.85rem;
    margin: 0;
    line-height: 1.4;
  }
`;
